"use client";

import { useState } from "react";
import { analyzeDecision, createDecision } from "@/lib/api";
import { useDataRefresh } from "@/lib/store";

interface Props {
  onClose: () => void;
  onCreated: (notice: string) => void;
}

export default function NewDecisionModal({ onClose, onCreated }: Props) {
  const [question, setQuestion] = useState("");
  const [context, setContext] = useState("");
  const [options, setOptions] = useState([{ name: "" }, { name: "" }]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzeAfter] = useState(true);
  // BUG_EVIDENCE_PACKET_2026-05-25 LIBRARY-DECISION P1: bumping
  // refreshNonce alone wasn't waking up every downstream consumer
  // of decisions. Fire the goal-change notifier too so subscribers
  // added later (DecisionThreadsScreen, etc.) refetch.
  const notifyGoalChange = useDataRefresh((s) => s.notifyGoalChange);

  const addOption = () => {
    setOptions((current) => (current.length < 6 ? [...current, { name: "" }] : current));
  };

  const updateOption = (index: number, value: string) => {
    setOptions((current) => {
      const updated = [...current];
      updated[index] = { ...updated[index], name: value };
      return updated;
    });
  };

  const removeOption = (index: number) => {
    setOptions((current) => (current.length > 2 ? current.filter((_, i) => i !== index) : current));
  };

  const canSubmit =
    question.trim().length > 0 &&
    options.filter((option) => option.name.trim().length > 0).length >= 2;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setError(null);

    try {
      const validOptions = options.filter((option) => option.name.trim().length > 0);
      const trimmedQuestion = question.trim();
      const decision = await createDecision(
        trimmedQuestion,
        context.trim() || null,
        validOptions
      );

      // codex follow-up 2026-05-25: the previous flow awaited
      // analyzeDecision before closing the modal, so the user sat
      // staring at "Creating…" for the entire LLM call. The decision
      // row already exists at this point — close the modal, let the
      // Decisions list show the new card immediately, and run the
      // analysis in the background. The card will refresh again
      // when analysis lands (notifyGoalChange fires once analysis
      // resolves, prompting DecisionThreadsScreen to re-fetch).
      const creationNotice = analyzeAfter
        ? `Added ${trimmedQuestion} to Review. AI analysis is running…`
        : `Added ${trimmedQuestion} to Review.`;
      notifyGoalChange();
      onCreated(creationNotice);
      onClose();

      if (analyzeAfter) {
        // Fire-and-forget the analysis. On success, re-fire
        // notifyGoalChange so DecisionThreadsScreen re-renders with
        // the new ai_reasoning / pros / cons / judgment. On failure,
        // we silently swallow — the decision is already on Decide
        // and the user can re-run Analyze from the card.
        void (async () => {
          try {
            await analyzeDecision(decision.id);
            notifyGoalChange();
          } catch (analyzeError) {
            console.error(
              "Decision was created, but AI analysis failed:",
              analyzeError,
            );
          }
        })();
      }
    } catch (submitError) {
      console.error("Failed to create decision:", submitError);
      setError(submitError instanceof Error ? submitError.message : "Failed to create decision");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid="new-decision-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 lg:left-60"
    >
      <div
        data-testid="new-decision-backdrop"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between p-5 pb-0">
          <h2 className="text-lg font-bold text-text">New decision</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted hover:bg-secondary/50"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text">
              What do you need to decide?
            </label>
            <input
              type="text"
              data-testid="decision-question-input"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="e.g. Should I take this job offer?"
              className="w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-text">
              Context <span className="font-normal text-muted">(optional)</span>
            </label>
            <textarea
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder="Any relevant background..."
              rows={3}
              className="w-full resize-none rounded-xl border border-border bg-white px-3.5 py-2.5 text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-text">Options</label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    data-testid={`decision-option-input-${index}`}
                    value={option.name}
                    onChange={(event) => updateOption(index, event.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 rounded-xl border border-border bg-white px-3.5 py-2.5 text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {options.length > 2 && (
                    <button
                      onClick={() => removeOption(index)}
                      className="rounded-lg p-2 text-muted transition-colors hover:bg-rose/10 hover:text-rose"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {options.length < 6 && (
              <button
                onClick={addOption}
                className="mt-2 flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add option
              </button>
            )}
          </div>

          {error && (
            <p className="rounded-xl border border-rose/20 bg-rose/8 px-3 py-2 text-sm text-rose">
              {error}
            </p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit || creating}
            data-testid="create-decision-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Creating...
              </>
            ) : (
              <>
                <span>+</span>
                Create decision
              </>
            )}
          </button>
          {!canSubmit && (
            <p className="text-xs text-muted">
              Add a question and at least two options to unlock AI analysis.
            </p>
          )}
          {canSubmit && (
            <p className="text-xs text-muted">
              We&apos;ll try to add AI analysis right away if it&apos;s available.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
